import _ from 'underscore';
import Promise from 'bluebird';

import { getBingMapColors } from './get-bing-map-colors';
import { TARGETING_TYPE } from '../util';

/**
 * Renders an instance of a Bing Maps V8 Web Control UI into the DOM. Manages and renders
 * user-provided map data such as location targets, tooltips, etc.
 * See: https://docs.microsoft.com/en-us/bingmaps/v8-web-control/
 */
export class BingMap {
  constructor({
    element,
    onGetTooltipHtmlContent,
    MapsApiHelper,
  }) {
    this.MapsApiHelper = MapsApiHelper;

    this.colors = getBingMapColors(this.Maps);

    const center = new this.Maps.Location(45.5, -122.5); // TODO: There could be predefined location
    const mapOptions = {
      center,
      credentials: this.MapsApiHelper.credentials,
      enableSearchLogo: false,
      mapTypeId: this.Maps.MapTypeId.road,
      navigationBarMode: this.Maps.NavigationBarMode.compact,
      restrictKeyboardListeningToMapContainer: true,
      showBreadcrumb: false,
      showDashboard: true,
      showLocateMeButton: false,
      showMapTypeSelector: false,
      showScalebar: true,
      zoom: 7,
    };

    this.polygonLayer = new this.Maps.Layer({ zIndex: 100 });
    this.pushpinLayer = new this.Maps.Layer({ zIndex: 200 });

    this.map = this.Maps.Map(element, mapOptions);
    this.map.layers.insertAll([this.polygonLayer, this.pushpinLayer]);

    this.onGetTooltipHtmlContent = onGetTooltipHtmlContent;
    if (_.isFunction(this.onGetTooltipHtmlContent)) {
      this.tooltip = new this.Maps.Infobox(
        new this.Maps.Location(0, 0),
        {
          showPointer: true,
          showCloseButton: false,
          visible: false,
        }
      );
      this.tooltip.setMap(this.map);
    }
  }

  get Maps() {
    return this.MapsApiHelper.Maps;
  }

  setLocationTargets({
    locationTargets,
    negativeLocationTargets,
  }) {
    // TODO: Add support for new location targets (search results) and recommended location targets
    this.polygonLayer.clear();
    this.pushpinLayer.clear();

    const addIncludedTargetsTasks = _.map(locationTargets, t => this.addLocationTargetToMap(t));
    const addExcludedTargetsTasks = _.map(
      negativeLocationTargets,
      t => this.addLocationTargetToMap(t)
    );

    return Promise.all([...addIncludedTargetsTasks, ...addExcludedTargetsTasks]);
  }

  addLocationTargetToMap(locationTarget) {
    let p$addPolygons = null;
    if (locationTarget.LocationType === 'MetroArea'
    || locationTarget.targetingType === TARGETING_TYPE.Top) {
      // if metro area, display only the pushpin
      p$addPolygons = Promise.resolve([]);
    } else {
      // generate a polygon for either radius or boundary target and add to map
      p$addPolygons = this.getPolygons(locationTarget).then((polygons) => {
        if (!_.isEmpty(polygons)) {
          this.polygonLayer.add(polygons);
        }

        return polygons;
      });
    }

    return p$addPolygons.then((polygons) => {
      if (polygons == null) {
        return false;
      }

      // add pushpin to map
      const point = new this.Maps.Location(locationTarget.Latitude, locationTarget.Longitude);
      let pushpin = null;
      if (locationTarget.topLocation && locationTarget.topLocation.getCustomPushpin) {
        pushpin = locationTarget.topLocation.getCustomPushpin({
          Maps: this.Maps,
        });
      } else {
        pushpin = new this.Maps
          .Pushpin(point, this.colors.pushpin[locationTarget.targetingType]);
      }

      if (this.tooltip) {
        const hoverElement = pushpin || polygons;
        this.Maps.Events.addHandler(hoverElement, 'mouseover', e => this.showTooltip(e, locationTarget));
        this.Maps.Events.addHandler(hoverElement, 'mouseout', e => this.hideTooltip(e));
      }

      if (pushpin) {
        this.pushpinLayer.add(pushpin);
      }

      return true;
    });
  }

  getPolygons(locationTarget) {
    const polygonOptions = this.colors.polygon[locationTarget.targetingType];
    return this.MapsApiHelper.getPolygons(locationTarget, polygonOptions);
  }

  showTooltip(event, locationTarget) {
    const location = new this.Maps.Location(locationTarget.Latitude, locationTarget.Longitude);
    const htmlContent = this.onGetTooltipHtmlContent(event, locationTarget);

    this.tooltip.setOptions({
      location,
      htmlContent,
      visible: true,
    });
  }

  hideTooltip() {
    this.tooltip.setOptions({ visible: false });
  }

  getViewBounds(locationTarget = null) {
    if (!locationTarget || !locationTarget.Latitude || !locationTarget.Longitude) {
      // NOTE: Avoid using "..." operator here, it will push the entire primitives array to the stack and potentially cause a stack overflow
      const shapes = this.pushpinLayer.getPrimitives().concat(this.polygonLayer.getPrimitives());
      return this.Maps.LocationRect.fromShapes(shapes);
    }

    // TODO: Get height/width from target's polygon
    const location = new this.Maps.Location(locationTarget.Latitude, locationTarget.Longitude);
    return new this.Maps.LocationRect(location, 0.5, 0.5);
  }

  zoomToFit(locationTarget) {
    const bounds = this.getViewBounds(locationTarget);
    this.map.setView({
      bounds,
      padding: 56,
    });

    if (bounds == null || (bounds.width === 0 && bounds.height === 0)) {
      this.map.setView({ zoom: 1 });
    }

    const newBounds = this.map.getBounds();

    // workarounds for buggy map behavior, zoom out to show the entire map if
    //   newBounds width is >= 360 degrees
    //   or newBounds is even smaller than the original bounds
    if (newBounds.width >= 360 ||
      (bounds != null && newBounds.width < bounds.width && newBounds.height < bounds.height)) {
      this.map.setView({ zoom: 1 });
    }
  }
}
